import { objectsAreEqual } from '../../helpers/utils'
import {
  ApiCallState,
  callFailed,
  callInitiated,
  callSucceeded
} from '../../types'
import { addOrUpdateElement } from '../helpers'
import { SecuritySearchResult } from '../searchSecurities/types'
import { WatchListsAction } from './actions'
import { WatchList, WatchlistDetails } from './types'

export interface State {
  watchLists: WatchList[] | undefined
  watchlistsDetails: Record<number, WatchlistDetails>
  pending: boolean
  error: boolean
  detailsCallState: ApiCallState
  lastCheckedSecurity: Record<number, number>
  checkedSecurities: Record<number, Set<number>>
  adminWatchlists: WatchList[] | undefined
  failedAdminCusipsAndIsins: string[]
  backgroundDetails: SecuritySearchResult[]
}

export const initialState: State = {
  watchLists: undefined,
  watchlistsDetails: {},
  detailsCallState: callSucceeded,
  pending: false,
  error: false,
  lastCheckedSecurity: {},
  checkedSecurities: {},
  adminWatchlists: undefined,
  failedAdminCusipsAndIsins: [],
  backgroundDetails: []
}

export const reducer = (
  state = initialState,
  action: WatchListsAction
): State => {
  const watchListMatch = (watchListOne: WatchList, watchListTwo: WatchList) =>
    watchListOne.id === watchListTwo.id
  switch (action.type) {
    case 'watchlist.setLastChecked':
      return {
        ...state,
        lastCheckedSecurity: {
          ...state.lastCheckedSecurity,
          [action.payload.watchlistId]: action.payload.securityId
        }
      }
    case 'watchList.fetchWatchLists':
      return { ...state, pending: true, error: false }
    case 'watchList.fetchWatchListsFailure':
      return { ...state, pending: false, error: true }
    case 'watchList.fetchWatchListsSuccess': {
      let result = state.watchLists || []
      for (const incoming of action.payload.watchlists) {
        const index = result.findIndex((wl) => watchListMatch(wl, incoming))
        if (index === -1) {
          result = [...result, incoming]
        } else if (!objectsAreEqual(result[index], incoming)) {
          result = result.map((wl, i) => (i === index ? incoming : wl))
        }
      }
      return { ...state, watchLists: result, pending: false, error: false }
    }
    case 'watchList.fetchWatchlistDetails':
      return {
        ...state,
        detailsCallState: callInitiated
      }
    case 'watchList.fetchWatchlistDetailsFailure':
      return {
        ...state,
        detailsCallState: { ...callFailed, status: action.payload }
      }
    case 'watchList.fetchWatchlistDetailsSuccess':
      return {
        ...state,
        watchlistsDetails: {
          ...state.watchlistsDetails,
          [action.payload.watchlistId]: action.payload.watchlistDetails
        },
        detailsCallState: callSucceeded
      }
    case 'watchlist.checkOrUncheckSecurities': {
      const { watchlistId, securityIds, checked } = action.payload
      const existing = state.checkedSecurities[watchlistId]
      let updatedSet: Set<number>
      if (checked) {
        updatedSet = new Set(existing)
        securityIds.forEach((s) => updatedSet.add(s))
      } else {
        updatedSet = new Set(existing)
        securityIds.forEach((s) => updatedSet.delete(s))
      }
      return {
        ...state,
        checkedSecurities: {
          ...state.checkedSecurities,
          [watchlistId]: updatedSet
        }
      }
    }
    case 'watchlist.resetCheckedSecurities': {
      const { [action.payload.watchlistId]: _checked, ...remainingChecked } =
        state.checkedSecurities
      const { [action.payload.watchlistId]: _last, ...remainingLast } =
        state.lastCheckedSecurity
      return {
        ...state,
        checkedSecurities: remainingChecked,
        lastCheckedSecurity: remainingLast
      }
    }
    case 'watchlist.deleteWatchlist':
    case 'watchlist.hideWatchlist':
      return {
        ...state,
        watchLists: state.watchLists?.filter((wl) => wl.id !== action.payload),
        // todo: get deleteWatchlistSuccess action to fire from epic
        adminWatchlists: state.adminWatchlists?.filter(
          (watchlist) => watchlist.id !== action.payload
        )
      }

    case 'watchList.fetchAdminWatchLists':
    case 'watchList.fetchAdminWatchlistsByIdentifier':
    case 'watchList.fetchAdminWatchlistsByUser':
      return {
        ...state,
        adminWatchlists: undefined
      }
    case 'watchList.fetchAdminWatchListsSuccess':
    case 'watchList.fetchAdminWatchlistsByIdentifierSuccess':
    case 'watchList.fetchAdminWatchlistsByUserSuccess':
      return {
        ...state,
        adminWatchlists: action.payload.watchlists.reduce(
          (acc, currVal) => addOrUpdateElement(acc, currVal, watchListMatch),
          state.adminWatchlists || []
        )
      }
    case 'watchList.definitionUpdated':
      return {
        ...state,
        adminWatchlists: addOrUpdateElement(
          state.adminWatchlists || [],
          action.payload,
          watchListMatch
        )
      }
    case 'watchList.failedSymbolsAndCusipsFromWatchlistDefinitionUpdate':
      return {
        ...state,
        failedAdminCusipsAndIsins: action.payload
      }
    case 'watchList.clearFailedSymbolsAndCusipsFromWatchlistDefinitionUpdate':
      return {
        ...state,
        failedAdminCusipsAndIsins: []
      }
    case 'watchList.fetchBackgroundDetailsSuccess': {
      return {
        ...state,
        backgroundDetails: action.payload
      }
    }
    default:
      return state
  }
}
